<template>
  <div class="space-y-6">
    <h1 class="text-3xl font-bold text-neutral-900 dark:text-neutral-100">HomeBase Collection Requests</h1>

    <!-- Collection Requests Management -->
    <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft p-6">
      <h2 class="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-6">Collection Requests</h2>
      <div class="flex flex-wrap items-center gap-4 mb-6">
        <select
          v-model="filters.status"
          aria-label="Filter by status"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="any">All Status</option>
          <option value="requested">Requested</option>
          <option value="completed">Completed</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <input
          type="text"
          placeholder="Search Villa Number"
          aria-label="Search villa number"
          v-model="filters.householdId"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 transition-all duration-200"
        />

        <select
          v-model="filters.assignedTo"
          aria-label="Filter by assigned user"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">All Users</option>
          <option v-for="user in users" :key="user.id" :value="user.id">{{ user.username }}</option>
        </select>

        <button
          @click="reload"
          class="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          Apply Filters
        </button>
      </div>

      <AlertBanner
        v-if="error && requests.length"
        type="error"
        message="Couldn't refresh collection requests — showing the last loaded list."
        :dismissible="false"
        class="mb-4"
      />
      <AlertBanner
        v-else-if="!loading && limitReached"
        type="info"
        :message="`Showing the first ${requests.length} results — narrow your filters to see the rest.`"
        :dismissible="false"
        class="mb-4"
      />

      <!-- Loading State -->
      <div v-if="loading" class="flex items-center justify-center py-12">
        <div class="flex flex-col items-center gap-3">
          <svg class="animate-spin h-10 w-10 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p class="text-sm text-neutral-600 dark:text-neutral-400">Loading collection requests...</p>
        </div>
      </div>

      <!-- Error State (nothing loaded yet) -->
      <ErrorState
        v-else-if="error && !requests.length"
        title="Couldn't load collection requests"
        :message="error"
        @retry="reload"
      />

      <!-- Empty State -->
      <div v-else-if="!sortedAndFiltered.length" class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <span class="material-icons text-5xl text-neutral-400 dark:text-neutral-600" aria-hidden="true">inbox</span>
          <p class="text-neutral-600 dark:text-neutral-400">No collection requests found</p>
        </div>
      </div>

      <!-- Data Table -->
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  :indeterminate="selected.length > 0 && selected.length < sortedAndFiltered.length"
                  @change="toggleAll"
                  aria-label="Select all requests"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </th>
              <th :aria-sort="ariaSort('requestedAt')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('requestedAt')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Requested At
                  <span v-if="sortState.key === 'requestedAt'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('householdVilla')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('householdVilla')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Villa - Community
                  <span v-if="sortState.key === 'householdVilla'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('containerSerial')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('containerSerial')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Container Serial
                  <span v-if="sortState.key === 'containerSerial'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('assignedTo')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('assignedTo')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Assigned To
                  <span v-if="sortState.key === 'assignedTo'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th :aria-sort="ariaSort('status')" class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">
                <button type="button" @click="sortBy('status')" class="inline-flex items-center gap-1 hover:text-primary-600 dark:hover:text-primary-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded transition-colors">
                  Status
                  <span v-if="sortState.key === 'status'" class="material-icons text-sm leading-none" aria-hidden="true">{{ sortState.dir === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</span>
                </button>
              </th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-neutral-700 dark:text-neutral-300">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-neutral-200 dark:divide-neutral-700">
            <tr v-for="request in sortedAndFiltered" :key="request.id" class="hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors">
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  :value="request.id"
                  v-model="selected"
                  :aria-label="`Select request ${request.householdVilla || request.id}`"
                  class="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500 dark:bg-neutral-800"
                />
              </td>
              <td class="px-4 py-3 text-sm text-neutral-700 dark:text-neutral-300">{{ formatDateTime(request.requestedAt || request.createdAt) }}</td>
              <td class="px-4 py-3 text-sm">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  <template v-if="request.householdVilla">
                    {{ request.householdVilla }}<span v-if="request.householdCommunity"> - {{ request.householdCommunity }}</span>
                  </template>
                  <template v-else>
                    {{ request.householdId }}
                  </template>
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                  {{ request.containerSerial || request.containerId }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span
                  v-if="request.assignedTo"
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                >
                  {{ users.find(u => u.id === request.assignedTo)?.username || request.assignedTo }}
                </span>
                <span
                  v-else
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200"
                >
                  Unassigned
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                  :class="{
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': request.status === 'requested',
                    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': request.status === 'completed',
                    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': request.status === 'delivered',
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': request.status === 'cancelled',
                    'bg-neutral-100 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-200': !['requested', 'completed', 'delivered', 'cancelled'].includes(request.status)
                  }"
                >
                  {{ request.status }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm">
                <div class="flex items-center gap-2">
                  <button
                    v-if="request.status === 'requested'"
                    @click="assignSingle(request)"
                    :disabled="assigningId === request.id || bulkAssigning"
                    class="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg v-if="assigningId === request.id" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Assign
                  </button>
                  <button
                    v-if="request.status === 'requested'"
                    @click="cancelRequest(request)"
                    class="text-sm px-3 py-1.5 bg-yellow-500 text-yellow-950 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500 focus-visible:ring-offset-2"
                  >
                    Cancel
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center gap-4 mt-6">
        <button
          @click="assignBulk"
          :disabled="!selected.length || !assignmentUser || bulkAssigning"
          class="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors duration-200 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg v-if="bulkAssigning" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ bulkAssigning ? 'Assigning…' : `Assign Selected to ${assignmentUser ? (users.find(u => u.id === assignmentUser)?.username || 'User') : 'User'}` }}
        </button>

        <select
          v-model="assignmentUser"
          aria-label="Assign to user"
          class="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 transition-all duration-200"
        >
          <option value="">Select User</option>
          <option v-for="user in users" :key="user.id" :value="user.id">{{ user.username }}</option>
        </select>

        <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200">
          Showing: {{ requests.length }}
        </span>
      </div>
    </div>

    <ConfirmDialog
      :show="!!requestToCancel"
      title="Cancel collection request?"
      :message="`Cancel the collection request for ${requestToCancel?.householdVilla || requestToCancel?.householdId || 'this household'}? This cannot be undone.`"
      confirm-label="Cancel Request"
      cancel-label="Keep Request"
      :loading="cancelLoading"
      danger
      @confirm="confirmCancelRequest"
      @cancel="requestToCancel = null"
    />
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import useHomebaseCollectionRequests from '@/composables/useHomebaseCollectionRequests'
import useToast from '@/composables/useToast'
import ErrorState from '@/components/ui/ErrorState.vue'
import AlertBanner from '@/components/ui/AlertBanner.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'

export default {
  name: 'HomeBaseCollectionRequests',
  components: { ErrorState, AlertBanner, ConfirmDialog },
  setup() {
    const {
      requests,
      users,
      loading,
      error,
      params,
      fetchRequests,
      fetchUsers,
      assignRequest,
      updateRequestStatus
    } = useHomebaseCollectionRequests()

    const { showToast } = useToast()

    const filters = reactive({
      status: 'requested',
      householdId: '',
      assignedTo: ''
    })

    const selected = ref([])
    const assignmentUser = ref('')

    const applyFilters = () => {
      params.value.status = filters.status === 'any' ? '' : filters.status
      params.value.householdId = filters.householdId.trim()
      params.value.assignedTo = filters.assignedTo
      fetchRequests()
    }

    const reload = () => {
      applyFilters()
    }

    const sortState = reactive({ key: 'requestedAt', dir: 'desc' })
    // Client-side sort only — mutating params here triggered a redundant
    // server refetch on top of the local sort.
    const sortBy = (key) => {
      if (sortState.key === key) {
        sortState.dir = sortState.dir === 'asc' ? 'desc' : 'asc'
      } else {
        sortState.key = key
        sortState.dir = 'asc'
      }
    }

    const ariaSort = (key) =>
      sortState.key === key ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : 'none'

    const sortedAndFiltered = computed(() => {
      return [...requests.value].sort((a, b) => {
        const va = (a[sortState.key] || '')
        const vb = (b[sortState.key] || '')
        if (va < vb) return sortState.dir === 'asc' ? -1 : 1
        if (va > vb) return sortState.dir === 'asc' ? 1 : -1
        return 0
      })
    })

    const limitReached = computed(() => requests.value.length >= (params.value.limit || 100))

    const formatDateTime = (value) => (value ? value.slice(0, 16).replace('T', ' ') : '-')

    const allSelected = computed(() => selected.value.length > 0 && selected.value.length === sortedAndFiltered.value.length)
    const toggleAll = (e) => {
      if (e.target.checked) selected.value = sortedAndFiltered.value.map(r => r.id)
      else selected.value = []
    }

    const assigningId = ref(null)
    const bulkAssigning = ref(false)

    const assignSingle = async (request) => {
      if (!assignmentUser.value) {
        showToast('info', 'Select a user from the dropdown first')
        return
      }
      if (assigningId.value) return
      assigningId.value = request.id
      try {
        await assignRequest(request.id, assignmentUser.value)
        showToast('success', 'Request assigned')
      } catch (err) {
        showToast('error', 'Failed to assign request. Please try again.')
      } finally {
        assigningId.value = null
      }
    }

    const assignBulk = async () => {
      if (!selected.value.length || !assignmentUser.value || bulkAssigning.value) return

      bulkAssigning.value = true
      try {
        const promises = selected.value.map(requestId => assignRequest(requestId, assignmentUser.value))
        await Promise.all(promises)
        const count = promises.length
        selected.value = []
        showToast('success', `${count} request${count === 1 ? '' : 's'} assigned`)
      } catch (err) {
        showToast('error', 'Failed to assign the selected requests. Please try again.')
      } finally {
        bulkAssigning.value = false
      }
    }

    const requestToCancel = ref(null)
    const cancelLoading = ref(false)

    const cancelRequest = (request) => {
      requestToCancel.value = request
    }

    const confirmCancelRequest = async () => {
      if (!requestToCancel.value || cancelLoading.value) return
      cancelLoading.value = true
      try {
        await updateRequestStatus(requestToCancel.value.id, 'cancelled', 'Cancelled by operations team', 'ops_user')
        requestToCancel.value = null
        showToast('success', 'Request cancelled')
      } catch (err) {
        showToast('error', 'Failed to cancel request. Please try again.')
      } finally {
        cancelLoading.value = false
      }
    }

    onMounted(() => {
      fetchRequests()
      fetchUsers()
    })

    return {
      requests,
      users,
      loading,
      error,
      filters,
      selected,
      assignmentUser,
      reload,
      sortedAndFiltered,
      sortBy,
      sortState,
      ariaSort,
      limitReached,
      formatDateTime,
      allSelected,
      toggleAll,
      assigningId,
      bulkAssigning,
      assignSingle,
      assignBulk,
      cancelRequest,
      requestToCancel,
      cancelLoading,
      confirmCancelRequest
    }
  }
}
</script>
